import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { SjtEditor } from "@/pages/job/SjtEditor";
import { InviteTracker } from "@/pages/assessments/InviteTracker";
import { SendAssessmentDialog } from "@/pages/assessments/SendAssessmentDialog";
import { JobAssessmentCard } from "@/pages/job/JobAssessmentCard";
import { CollapsibleSection } from "@/components/app/CollapsibleSection";
import { confirmAction } from "@/components/app/Confirm";

function Setup({ job }) {
  const qc = useQueryClient();
  const cfg = job.assessment_config;
  const save = async (patch) => {
    try { await api.patch(`/jobs/${job.id}`, { assessment_config: { ...cfg, ...patch } }); qc.invalidateQueries({ queryKey: ["job", job.id] }); toast.success("Assessment settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="panel flex flex-wrap items-end gap-4 p-5" data-testid="assessment-setup">
      <label className="space-y-1 text-sm"><span className="text-ink-secondary">Questionnaire</span>
        <select value={cfg.instrument} onChange={async (e) => { const v = e.target.value; if (v === "ipip_neo_120" && !(await confirmAction({ title: "Switch to the full research version?", body: "It contains political and religious statements and is not recommended for hiring.", entity: "IPIP-NEO-120 (full research version)", confirmLabel: "Switch anyway" }))) return; save({ instrument: v, instrument_explicit: v === "ipip_neo_120" }); }} className="block h-9 rounded-lg border border-line bg-white px-2" data-testid="instrument-select">
          <option value="ipip_neo_120_safe">IPIP-NEO-120 (hiring-safe) · about 25 min</option><option value="ipip_50">IPIP-50 · short version · about 12 min</option>
          <option value="ipip_neo_120">Full research version (not recommended for hiring)</option>
        </select></label>
      <label className="space-y-1 text-sm"><span className="text-ink-secondary">Link expires after</span>
        <select value={cfg.expiry_days ?? ""} onChange={(e) => save({ expiry_days: e.target.value ? Number(e.target.value) : null })} className="block h-9 rounded-lg border border-line bg-white px-2" data-testid="expiry-select">
          <option value="" label="Never (default)" />
          {[3, 5, 7, 10, 14].map((d) => <option key={d} value={d} label={`After ${d} days`} />)}
        </select></label>
      <p className="text-xs text-ink-muted">Public-domain IPIP items · scored by fixed rules against published norms · changes apply to new invites.</p>
    </section>
  );
}

function SendPanel({ job }) {
  const { data } = useQuery({ queryKey: ["ranked", job.id], queryFn: () => get(`/jobs/${job.id}/ranked`) });
  const [sel, setSel] = useState(new Set());
  const [open, setOpen] = useState(false);
  const { data: att } = useQuery({ queryKey: ["job-assessment", job.id], queryFn: () => get(`/jobs/${job.id}/assessment-template`) });
  const tpl = att?.template;
  const ready = !!tpl && !tpl.warnings?.length;
  const why = !tpl ? "Choose an assessment for this job first (card above)." : !ready ? "Fix the attached assessment first (see the card above)." : null;
  const rows = (data?.rows || []).filter((r) => r.stage === "screen_shortlist" && r.shortlisted);
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  return (
    <section className="panel space-y-3 p-5" data-testid="send-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold">Shortlisted, not yet assessed</h3><span className="text-sm text-ink-muted">{rows.length}</span>
        <Button size="sm" className="ml-auto" disabled={!sel.size || !ready} title={why || undefined} onClick={() => setOpen(true)} data-testid="send-assessment-btn"><Send size={14} /> Send assessment ({sel.size})</Button>
      </div>
      {why && <p className="text-sm text-caution" role="status" data-testid="send-not-ready">{why}</p>}
      {ready && <p className="text-xs text-ink-muted" data-testid="send-using">Sends “{tpl.name}” · {tpl.section_titles.join(" · ")}</p>}
      {!rows.length ? <p className="text-sm text-ink-muted">Shortlist candidates on the Candidates tab to invite them here.</p> : (
        <ul className="divide-y divide-line rounded-[6px] border border-line">
          {rows.map((r) => (
            <li key={r.application_id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <Checkbox checked={sel.has(r.application_id)} onCheckedChange={() => toggle(r.application_id)} data-testid={`send-select-${r.application_id}`} />
              <span className="flex-1">{r.name}</span><span className="font-display font-semibold tabular-nums">{r.resume_match ?? "—"}</span>
            </li>
          ))}
        </ul>
      )}
      <SendAssessmentDialog jobId={job.id} applicationIds={[...sel]} open={open} onClose={() => setOpen(false)} onSent={() => setSel(new Set())} />
    </section>
  );
}

export function AssessmentsTab({ job }) {
  const { user } = useAuth();
  const staff = STAFF.includes(user.role);
  return (
    <div className="space-y-6" data-testid="job-assessments-tab">
      <JobAssessmentCard job={job} staff={staff} />
      <SjtEditor jobId={job.id} canEdit={staff} />
      {staff && <SendPanel job={job} />}
      {staff && <CollapsibleSection title="Advanced: questionnaire version and link expiry" defaultOpen={false} testId="assessment-advanced"><div className="p-1"><Setup job={job} /></div></CollapsibleSection>}
      <InviteTracker url={`/jobs/${job.id}/assessments`} queryKey={["job-assessments", job.id]} />
    </div>
  );
}
